import { createContext, useContext, useEffect, useState } from 'react'
import { getAuthProvider } from './auth-config'
import {
  AccessSuspendedError,
  IdentityRefusalError,
  fetchUserIdentity,
  fetchUserTenants,
  switchTenant as switchTenantApi,
  type IdentityRefusalCode,
} from './user-identity-api'
import { getCachedUserIdentity, setCachedUserIdentity, clearCachedUserIdentity } from './user-identity-cache'
import type { AuthUser } from './auth-provider'

interface AuthContextValue {
  user: AuthUser | null
  isAuthenticated: boolean
  isInitialized: boolean
  isSwitchingTenant: boolean
  serverError: string | null
  /**
   * Set when th_saas refused the identity on a first login (401
   * `InvitationNotValid`, UC-084.1). Not a server error: the AuthGuard sends
   * the person to the invitation outcome page instead of starting a login.
   */
  identityRefusal: IdentityRefusalCode | null
  /**
   * Set when the bootstrap identity load answered 403 `UserAccessSuspended`
   * (UC-084.3 E2). The session user is kept so the layout renders and its
   * dialog offers the other tenants instead of a sign-in redirect.
   */
  accessSuspended: boolean
  login: () => Promise<void>
  logout: () => Promise<void>
  editProfile: () => Promise<void>
  switchTenant: (tenantId: number) => Promise<void>
  getToken: () => Promise<string | null>
}

const AuthContext = createContext<AuthContextValue | null>(null)

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const [isSwitchingTenant, setIsSwitchingTenant] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const [identityRefusal, setIdentityRefusal] = useState<IdentityRefusalCode | null>(null)
  const [accessSuspended, setAccessSuspended] = useState(false)
  const provider = getAuthProvider()

  useEffect(() => {
    // Shared by both identity loads below. Returns true when the failure was a
    // refusal and has been handled, so the caller skips its generic path.
    function applyIdentityRefusal(error: unknown): boolean {
      if (!(error instanceof IdentityRefusalError)) return false
      clearCachedUserIdentity()
      setUser(null)
      setIdentityRefusal(error.code)
      return true
    }

    // Identity and tenants load together; allSettled keeps the tenants when only
    // the identity is refused, so a suspended person can still switch.
    async function loadIdentity(token: string): Promise<AuthUser | null> {
      const [identityResult, tenantsResult] = await Promise.allSettled([
        fetchUserIdentity(token),
        fetchUserTenants(token),
      ])
      const failures = [identityResult, tenantsResult]
        .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
        .map((result) => result.reason as unknown)
      const tenants = tenantsResult.status === 'fulfilled' ? tenantsResult.value : []
      if (failures.some((failure) => failure instanceof AccessSuspendedError)) {
        const sessionUser = getCachedUserIdentity() ?? provider.getUser()
        if (!sessionUser) throw new AccessSuspendedError()
        setUser({ ...sessionUser, tenants })
        setAccessSuspended(true)
        return null
      }
      const refusal = failures.find((failure) => failure instanceof IdentityRefusalError)
      if (failures.length > 0 || identityResult.status === 'rejected') throw refusal ?? failures[0]
      const enriched = { ...identityResult.value, tenants }
      setUser(enriched)
      setCachedUserIdentity(enriched)
      setIdentityRefusal(null)
      setAccessSuspended(false)
      return enriched
    }

    const unsubscribe = provider.onAuthStateChange((claimsUser) => {
      if (!claimsUser) {
        clearCachedUserIdentity()
        setUser(null)
        return
      }
      // Re-fetch identity when B2C notifies a new account (e.g. after redirect)
      provider.getToken().then((token) => {
        if (!token) return
        loadIdentity(token)
          .catch((err: unknown) => {
            if (applyIdentityRefusal(err)) return
            console.error('[Auth] Failed to enrich user identity on state change', err)
          })
      }).catch(() => { /* no token */ })
    })

    provider
      .initialize()
      .then(async () => {
        const basicUser = provider.getUser()

        if (!basicUser) {
          clearCachedUserIdentity()
          setUser(null)
          setIsInitialized(true)
          return
        }

        // Show cached user immediately for faster perceived load
        const cached = getCachedUserIdentity()
        if (cached) setUser(cached)

        try {
          const token = await provider.getToken()
          if (!token) throw new Error('No token available')
          const enriched = await loadIdentity(token)
          if (enriched) console.debug('[Auth] Fetched user identity:', enriched.email)
        } catch (err: unknown) {
          if (applyIdentityRefusal(err)) return
          console.error('[Auth] Failed to fetch user identity', err)
          // Server is unreachable or returned an error — clear state and redirect to login
          clearCachedUserIdentity()
          setUser(null)
          setServerError(
            err instanceof TypeError
              ? 'Unable to reach the server. Please check your connection.'
              : 'Server error. Please try again later.'
          )
        } finally {
          setIsInitialized(true)
        }
      })
      .catch((err: unknown) => {
        console.error('[Auth] initialization failed', err)
        setIsInitialized(true)
      })

    return unsubscribe
  }, [provider])

  const value: AuthContextValue = {
    user,
    isAuthenticated: user !== null,
    isInitialized,
    serverError,
    identityRefusal,
    accessSuspended,
    login: () => provider.login(),
    logout: async () => {
      clearCachedUserIdentity()
      await provider.logout()
    },
    editProfile: () => provider.editProfile(),
    isSwitchingTenant,
    switchTenant: async (tenantId: number) => {
      setIsSwitchingTenant(true)
      try {
        const token = await provider.getToken()
        if (!token) throw new Error('No token available')
        await switchTenantApi(token, tenantId)
        clearCachedUserIdentity()
        window.location.reload()
      } catch (err) {
        setIsSwitchingTenant(false)
        throw err
      }
    },
    getToken: () => provider.getToken(),
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
